import React, { useEffect, useContext, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { Navbar, Nav, Container, NavDropdown } from 'react-bootstrap';
import { FaFacebook, FaInstagram, FaYoutube, FaWhatsapp, FaShoppingCart, FaArrowUp } from 'react-icons/fa';
import { CartProvider, CartContext } from './contexts/CartContext';
import Home from './components/Home';
import About from './components/About';
import Contact from './components/Contact';
import Cart from './components/Cart';
import Hormigon from './components/Hormigon';
import Materiales from './components/Materiales';
import Suelos from './components/Suelos';
import Pisos from './components/Pisos';
import Footer from './components/Footer';
import './App.css';

function App() {
    const [expanded, setExpanded] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setExpanded(false);
    }, [location]);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Determinar si mostrar el botón de WhatsApp y otros elementos flotantes
    const showFloatingElements = !['/services/suelos', '/services/pisos'].includes(location.pathname);

    return (
        <CartProvider>
            <Helmet>
                <title>Darom SA - Soluciones Integrales en Construcción</title>
                <meta name="description" content="Darom SA ofrece hormigón elaborado, materiales de construcción, pisos industriales y servicios de estudio y movimiento de suelos." />
                <meta name="keywords" content="hormigón elaborado, construcción, materiales, pisos industriales, movimiento de suelos, estudio de suelos, zona sur de Buenos Aires" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        "name": "Darom SA",
                        "url": "https://www.daromsa.com.ar",
                        "logo": "https://www.daromsa.com.ar/ultimologodarom.png",
                        "contactPoint": {
                            "@type": "ContactPoint",
                            "telephone": "+542215739000",
                            "contactType": "sales",
                            "areaServed": "Argentina"
                        },
                        "sameAs": [
                            "https://www.facebook.com/DAROMSRL",
                            "https://www.instagram.com/daromsa/",
                            "https://www.youtube.com/watch?v=--7y8f63ZPk"
                        ]
                    })}
                </script>
            </Helmet>

            <div className="app-container">
                <Navbar bg="black" expand="lg" className="navbar-custom" expanded={expanded}>
                    <Container fluid>
                        <Navbar.Brand as={Link} to="/" className="me-0 me-lg-3">
                            <img 
                                src="/ultimologodarom.png" 
                                alt="Darom SA - Especialistas en construcción" 
                                className="navbar-logo"
                                width="180"
                                height="60"
                                loading="lazy"
                            />
                        </Navbar.Brand>
                        
                        <Navbar.Toggle 
                            aria-controls="main-navbar" 
                            onClick={() => setExpanded(!expanded)}
                            className="custom-toggler"
                            aria-label="Toggle navigation"
                        >
                            <span className="navbar-toggler-icon"></span>
                        </Navbar.Toggle>
                        
                        <Navbar.Collapse id="main-navbar">
                            <Nav className="mx-auto flex-wrap justify-content-center">
                                <Nav.Link as={Link} to="/services/hormigon" onClick={() => setExpanded(false)} className="nav-item-custom">Hormigón Elaborado</Nav.Link>
                                <Nav.Link as={Link} to="/services/materiales" onClick={() => setExpanded(false)} className="nav-item-custom">Materiales</Nav.Link>
                                <Nav.Link as={Link} to="/services/suelos" onClick={() => setExpanded(false)} className="nav-item-custom">Estudio y Movimiento de Suelos</Nav.Link>
                                <Nav.Link as={Link} to="/services/pisos" onClick={() => setExpanded(false)} className="nav-item-custom">Pisos Industriales</Nav.Link>
                                <Nav.Link as={Link} to="/about" onClick={() => setExpanded(false)} className="nav-item-custom">La Empresa</Nav.Link>
                                <Nav.Link as={Link} to="/contact" onClick={() => setExpanded(false)} className="nav-item-custom">Contacto</Nav.Link>
                            </Nav>
                            <div className="navbar-extra d-flex align-items-center">
                                <div className="social-links d-flex">
                                    <Nav.Link href="https://www.facebook.com/DAROMSRL" target="_blank" rel="noopener noreferrer nofollow" className="social-link" aria-label="Facebook">
                                        <FaFacebook className="social-icon" />
                                    </Nav.Link>
                                    <Nav.Link href="https://www.instagram.com/daromsa/" target="_blank" rel="noopener noreferrer nofollow" className="social-link" aria-label="Instagram">
                                        <FaInstagram className="social-icon" />
                                    </Nav.Link>
                                    <Nav.Link href="https://www.youtube.com/watch?v=--7y8f63ZPk" target="_blank" rel="noopener noreferrer nofollow" className="social-link" aria-label="YouTube">
                                        <FaYoutube className="social-icon" />
                                    </Nav.Link>
                                </div>
                                <CartSummary />
                            </div>
                        </Navbar.Collapse>
                    </Container>
                </Navbar>
                
                <main className="main-content">
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/services/materiales" element={<Materiales />} />
                        <Route path="/services/hormigon" element={<Hormigon />} />
                        <Route path="/services/suelos" element={<Suelos />} />
                        <Route path="/services/pisos" element={<Pisos />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/cart" element={<Cart />} />
                    </Routes>
                </main>

                {showFloatingElements && (
                    <>
                        <a 
                            href="https://wa.me/5492215739000?text=Hola%20Darom%20SA,%20vi%20su%20página%20web%20y%20me%20interesa%20solicitar%20información" 
                            target="_blank" 
                            rel="noopener noreferrer nofollow" 
                            className="whatsapp-float" 
                            aria-label="Contactar por WhatsApp"
                        >
                            <FaWhatsapp className="whatsapp-icon" />
                        </a>

                        <button 
                            className="scroll-to-top" 
                            onClick={scrollToTop}
                            aria-label="Volver arriba"
                        >
                            <FaArrowUp />
                        </button>
                    </>
                )}

                <Footer />
            </div>
        </CartProvider>
    );
}

function CartSummary() {
    const { cart } = useContext(CartContext);
    const totalItems = cart.reduce((total, item) => total + item.quantity, 0);
    const totalPrice = cart.reduce((acc, item) => acc + (item.price * item.quantity), 0);

    return (
        <NavDropdown 
            title={<>
                <FaShoppingCart aria-hidden="true" /> 
                {totalItems > 0 && <span className="cart-badge">{totalItems}</span>}
                <span className="visually-hidden">Carrito de compras</span>
            </>} 
            id="cart-dropdown"
            className="cart-dropdown"
            aria-label="Carrito de compras"
        >
            <NavDropdown.Item as={Link} to="/cart">Ver Carrito</NavDropdown.Item>
            <NavDropdown.Divider />
            <NavDropdown.Item disabled>Artículos: {totalItems}</NavDropdown.Item>
            <NavDropdown.Item disabled>Total: ${totalPrice.toFixed(2)}</NavDropdown.Item>
        </NavDropdown>
    );
}

export default App;